// Small charts for usage data. One series each, so one hue: the accent.
// Every chart carries an equivalent screen-reader table, values are text in
// ink colours (never the series colour), and the line chart has a hover /
// focus crosshair with a tooltip.

import { useId, useMemo, useState, type KeyboardEvent, type PointerEvent } from 'react'
import { cn, formatCount } from '@/lib/format'

// ─── Bar list ───────────────────────────────────────────────────────────────

export interface BarListItem {
  label: string
  value: number
  /** Optional second, smaller value drawn inside the bar (e.g. "used" of "on"). */
  inner?: number
}

export interface BarListProps {
  items: BarListItem[]
  /** Scale max (default: the largest value). */
  max?: number
  /** Accessible name. */
  label: string
  emptyText?: string
  /** "12 / 40" style values when a max is given. */
  showOfMax?: boolean
  /** Column header for the value in the screen-reader table. */
  valueLabel?: string
  /** Column header for the inner value in the screen-reader table. */
  innerLabel?: string
  /** Words between the inner value and the value on screen: "3 used of 5". */
  innerJoiner?: string
  className?: string
}

/** Horizontal bars, label left, value right. Rounded data-ends, recessive track. */
export function BarList({ items, max, label, emptyText = 'No data', showOfMax, valueLabel = 'Value', innerLabel, innerJoiner = 'of', className }: BarListProps) {
  if (items.length === 0) return <p className="text-xs text-muted">{emptyText}</p>
  const scale = max ?? Math.max(1, ...items.map((i) => i.value))
  return (
    <figure className={cn('m-0', className)}>
      <ul aria-hidden className="grid gap-3">
        {items.map((it) => {
          const pct = scale ? Math.min(100, (it.value / scale) * 100) : 0
          const innerPct = scale && it.inner !== undefined ? Math.min(100, (it.inner / scale) * 100) : 0
          return (
            <li key={it.label} className="grid gap-1.5">
              <div className="flex items-baseline justify-between gap-2 text-[13px]">
                <span className="min-w-0 truncate font-semibold text-ink">{it.label}</span>
                <span className="shrink-0 font-mono text-xs text-muted tabular">
                  {it.inner !== undefined ? (
                    <>
                      <span className="text-ink">{formatCount(it.inner)}</span>
                      <span className="text-subtle"> {innerJoiner} </span>
                    </>
                  ) : null}
                  <span className={it.inner !== undefined ? 'text-muted' : 'text-ink'}>{formatCount(it.value)}</span>
                  {showOfMax && max !== undefined ? <span className="text-subtle">/{formatCount(max)}</span> : null}
                </span>
              </div>
              <div className="relative h-2 overflow-hidden rounded-full bg-toggle-off">
                {/* With an inner value the outer bar is the faint "of" share; alone it's the solid accent. */}
                <div className={cn('absolute inset-y-0 left-0 rounded-full', it.inner !== undefined ? 'bg-accent/35' : 'bg-accent')} style={{ width: `${pct}%` }} />
                {it.inner !== undefined ? <div className="absolute inset-y-0 left-0 rounded-full bg-accent" style={{ width: `${innerPct}%` }} /> : null}
              </div>
            </li>
          )
        })}
      </ul>
      {/* sr-only on a wrapper: a <table> grows to fit its content whatever its width, so
          sr-only on the table itself still widens the page (390px overflow). */}
      <div className="sr-only">
        <table>
          <caption>{label}</caption>
          <thead>
            <tr>
              <th scope="col">Item</th>
              <th scope="col">{valueLabel}</th>
              {innerLabel ? <th scope="col">{innerLabel}</th> : null}
            </tr>
          </thead>
          <tbody>
            {items.map((it) => (
              <tr key={it.label}>
                <th scope="row">{it.label}</th>
                <td>{it.value}</td>
                {innerLabel ? <td>{it.inner ?? ''}</td> : null}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </figure>
  )
}

// ─── Line chart ─────────────────────────────────────────────────────────────

export interface LineChartProps {
  data: Array<{ date: string; views: number }>
  label: string
  height?: number
  className?: string
}

const W = 600
const PAD = { top: 12, right: 8, bottom: 22, left: 8 }

function shortDay(iso: string) {
  const d = new Date(iso.length === 10 ? `${iso}T12:00:00` : iso)
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleDateString(undefined, { day: 'numeric', month: 'short' })
}

/** Daily series as a 2px line with a soft area, hover/arrow-key crosshair and tooltip. */
export function LineChart({ data, label, height = 160, className }: LineChartProps) {
  const [active, setActive] = useState<number | null>(null)
  const titleId = useId()
  const pts = useMemo(() => {
    const max = Math.max(1, ...data.map((d) => d.views))
    const innerW = W - PAD.left - PAD.right
    const innerH = height - PAD.top - PAD.bottom
    return data.map((d, i) => ({
      x: PAD.left + (data.length <= 1 ? innerW / 2 : (i / (data.length - 1)) * innerW),
      y: PAD.top + innerH - (d.views / max) * innerH,
      ...d,
    }))
  }, [data, height])

  if (data.length === 0) return <p className="text-xs text-muted">No page views recorded yet.</p>

  const line = pts.map((p, i) => `${i ? 'L' : 'M'}${p.x.toFixed(1)},${p.y.toFixed(1)}`).join(' ')
  const base = height - PAD.bottom
  const area = `${line} L${pts[pts.length - 1].x.toFixed(1)},${base} L${pts[0].x.toFixed(1)},${base} Z`
  const a = active !== null ? pts[active] : null

  const onMove = (e: PointerEvent<SVGSVGElement>) => {
    const box = e.currentTarget.getBoundingClientRect()
    const x = ((e.clientX - box.left) / box.width) * W
    let best = 0
    for (let i = 1; i < pts.length; i++) if (Math.abs(pts[i].x - x) < Math.abs(pts[best].x - x)) best = i
    setActive(best)
  }
  const onKey = (e: KeyboardEvent<SVGSVGElement>) => {
    if (e.key === 'ArrowRight') setActive((i) => Math.min(pts.length - 1, (i ?? -1) + 1))
    else if (e.key === 'ArrowLeft') setActive((i) => Math.max(0, (i ?? pts.length) - 1))
    else return
    e.preventDefault()
  }

  return (
    <figure className={cn('relative m-0', className)}>
      <svg
        viewBox={`0 0 ${W} ${height}`}
        preserveAspectRatio="none"
        className="block h-40 w-full touch-none overflow-visible"
        role="img"
        aria-labelledby={titleId}
        tabIndex={0}
        onPointerMove={onMove}
        onPointerLeave={() => setActive(null)}
        onFocus={() => setActive((i) => i ?? pts.length - 1)}
        onBlur={() => setActive(null)}
        onKeyDown={onKey}
      >
        <title id={titleId}>{`${label}. Use left and right arrow keys to read each day.`}</title>
        <line x1={PAD.left} x2={W - PAD.right} y1={base} y2={base} className="stroke-line" strokeWidth={1} vectorEffect="non-scaling-stroke" />
        <path d={area} className="fill-accent/10" />
        <path d={line} fill="none" className="stroke-accent" strokeWidth={2} strokeLinejoin="round" strokeLinecap="round" vectorEffect="non-scaling-stroke" />
        {a ? (
          <line x1={a.x} x2={a.x} y1={PAD.top} y2={base} className="stroke-line-strong" strokeWidth={1} strokeDasharray="3 3" vectorEffect="non-scaling-stroke" />
        ) : null}
      </svg>
      {a ? (
        <>
          <span
            aria-hidden
            className="pointer-events-none absolute h-2.5 w-2.5 -translate-x-1/2 -translate-y-1/2 rounded-full border-2 border-panel bg-accent"
            style={{ left: `${(a.x / W) * 100}%`, top: `${(a.y / height) * 100}%` }}
          />
          <div
            role="status"
            className="pointer-events-none absolute top-0 z-10 -translate-x-1/2 whitespace-nowrap rounded-md border border-line-strong bg-raised px-2 py-1 text-xs shadow-toast"
            style={{ left: `${Math.min(88, Math.max(12, (a.x / W) * 100))}%` }}
          >
            <span className="text-muted">{shortDay(a.date)}</span> <span className="font-semibold text-ink tabular">{formatCount(a.views)}</span>
            <span className="text-muted"> views</span>
          </div>
        </>
      ) : null}
      <div aria-hidden className="mt-1 flex justify-between text-2xs text-subtle tabular">
        <span>{shortDay(data[0].date)}</span>
        <span>{shortDay(data[data.length - 1].date)}</span>
      </div>
      <div className="sr-only">
        <table>
          <caption>{label}</caption>
          <thead>
            <tr>
              <th scope="col">Day</th>
              <th scope="col">Views</th>
            </tr>
          </thead>
          <tbody>
            {data.map((d) => (
              <tr key={d.date}>
                <th scope="row">{shortDay(d.date)}</th>
                <td>{d.views}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </figure>
  )
}

// ─── Sparkline ──────────────────────────────────────────────────────────────

/** Tiny trend line for table rows. Decorative: pair it with the number. */
export function Sparkline({ data, className }: { data: number[]; className?: string }) {
  if (data.length < 2) return null
  const max = Math.max(1, ...data)
  const w = 72
  const h = 20
  const d = data.map((v, i) => `${i ? 'L' : 'M'}${((i / (data.length - 1)) * w).toFixed(1)},${(h - 2 - (v / max) * (h - 4)).toFixed(1)}`).join(' ')
  return (
    <svg aria-hidden viewBox={`0 0 ${w} ${h}`} className={cn('h-5 w-[72px] shrink-0 overflow-visible', className)}>
      <path d={d} fill="none" className="stroke-accent" strokeWidth={1.5} strokeLinejoin="round" strokeLinecap="round" />
    </svg>
  )
}
